import { useState, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Upload, X, FileText, AlertCircle, CheckCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { PARCELAS, DOCUMENT_TYPE_LABELS } from '@/lib/constants'
import type { DocumentTypeEnum } from '@/types'

// =============================================================================
// DocumentUpload Component
// =============================================================================

interface Props {
  onClose:   () => void
  onSuccess: () => void
  defaultParcelId?:   string
  defaultInstanceId?: string
}

export default function DocumentUpload({ onClose, onSuccess, defaultParcelId, defaultInstanceId }: Props) {
  const fileInputRef                = useRef<HTMLInputElement>(null)
  const [dragging, setDragging]     = useState(false)
  const [selectedFile, setFile]     = useState<File | null>(null)
  const [docType, setDocType]       = useState<DocumentTypeEnum>('PPI')
  const [parcelNum, setParcelNum]   = useState<number | null>(null)
  const [instanceId, setInstanceId] = useState<string>(defaultInstanceId ?? '')
  const [versionNotes, setNotes]    = useState('')
  const [uploading, setUploading]   = useState(false)
  const [error, setError]           = useState<string | null>(null)
  const [success, setSuccess]       = useState(false)

  // Fetch activity instances for selected parcel
  const { data: instances } = useQuery({
    queryKey: ['activity-instances-for-upload', parcelNum],
    queryFn: async () => {
      if (!parcelNum) return []
      const { data, error } = await supabase
        .from('activity_instances')
        .select(`
          id,
          family:activity_families(name, code)
        `)
        .eq('is_active', true)
        .order('family(display_order)')
      if (error) return []
      // Filter by parcel in memory (simplified)
      return data ?? []
    },
    enabled: !!parcelNum,
  })

  // ---------------------------------------------------------------------------
  // Drag & drop handlers
  // ---------------------------------------------------------------------------
  const onDragOver  = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragging(true) }, [])
  const onDragLeave = useCallback(() => setDragging(false), [])
  const onDrop      = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) setFile(file)
  }, [])

  // ---------------------------------------------------------------------------
  // Upload handler
  // ---------------------------------------------------------------------------
  const handleUpload = async () => {
    if (!selectedFile) { setError('Selecciona un archivo.'); return }

    setUploading(true)
    setError(null)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('No autenticado')

      // Build storage path: documents/{parcel}/{type}/{timestamp}_{filename}
      const timestamp  = Date.now()
      const safeName   = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const parcelPath = parcelNum ? `parcela-${String(parcelNum).padStart(2, '0')}` : 'global'
      const storagePath = `${parcelPath}/${docType}/${timestamp}_${safeName}`

      // Upload file to Supabase Storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .upload(storagePath, selectedFile, { upsert: false })

      if (storageError) throw storageError

      // Get parcel ID
      let parcelId: string | null = null
      if (parcelNum) {
        const { data: parcelData } = await supabase
          .from('parcels')
          .select('id')
          .eq('parcel_number', parcelNum)
          .single()
        parcelId = parcelData?.id ?? null
      }

      // Insert document record
      const { error: insertError } = await supabase
        .from('documents')
        .insert({
          document_type:        docType,
          activity_instance_id: instanceId || null,
          parcel_id:            parcelId,
          uploaded_by:          user.id,
          filename:             selectedFile.name,
          storage_path:         storagePath,
          mime_type:            selectedFile.type,
          file_size_bytes:      selectedFile.size,
          status:               'PENDIENTE',
          current_version:      1,
          notes:                versionNotes || null,
        })

      if (insertError) throw insertError

      setSuccess(true)
      setTimeout(() => onSuccess(), 1500)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al subir el archivo')
    } finally {
      setUploading(false)
    }
  }

  const formatBytes = (bytes: number) => {
    if (bytes < 1024)       return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-panel w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Subir documento</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Success state */}
          {success ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
              <p className="text-sm font-semibold text-gray-800">Documento subido correctamente</p>
            </div>
          ) : (
            <>
              {/* Drop zone */}
              <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                  border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                  ${dragging ? 'border-brand-400 bg-brand-50' : 'border-gray-200 hover:border-brand-300 hover:bg-gray-50'}
                `}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx,.dwg"
                  onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-brand-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-800">{selectedFile.name}</p>
                      <p className="text-xs text-gray-400">{formatBytes(selectedFile.size)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null) }}
                      className="ml-auto p-1 rounded text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-700">
                      Arrastra tu archivo aquí o <span className="text-brand-600">selecciona</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      PDF, imágenes, Excel, Word, DWG · Máx. 50 MB
                    </p>
                  </>
                )}
              </div>

              {/* Document type */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Tipo de documento *
                </label>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value as DocumentTypeEnum)}
                  className="input-base text-sm"
                >
                  {Object.entries(DOCUMENT_TYPE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>

              {/* Parcel */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Parcela
                </label>
                <select
                  value={parcelNum ?? ''}
                  onChange={(e) => setParcelNum(e.target.value ? parseInt(e.target.value) : null)}
                  className="input-base text-sm"
                >
                  <option value="">Global / Sin parcela</option>
                  {PARCELAS.map((p) => (
                    <option key={p.number} value={p.number}>{p.displayName}</option>
                  ))}
                </select>
              </div>

              {/* Activity instance */}
              {parcelNum && instances && instances.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                    Actividad asociada
                  </label>
                  <select
                    value={instanceId}
                    onChange={(e) => setInstanceId(e.target.value)}
                    className="input-base text-sm"
                  >
                    <option value="">Sin actividad específica</option>
                    {instances.map((inst) => {
                      const f = (inst as { id: string; family: { name: string; code: string } }).family
                      return (
                        <option key={(inst as { id: string }).id} value={(inst as { id: string }).id}>
                          {f?.code}. {f?.name}
                        </option>
                      )
                    })}
                  </select>
                </div>
              )}

              {/* Version notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Notas de versión
                </label>
                <textarea
                  value={versionNotes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Descripción del documento, revisión, etc."
                  className="input-base text-sm resize-none"
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <p className="text-xs">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <button onClick={onClose} className="btn-secondary text-sm">
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="btn-primary text-sm"
            >
              {uploading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {uploading ? 'Subiendo...' : 'Subir documento'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
